import { Controller } from "@hotwired/stimulus";

const HIGHLIGHT_CLASS = "ticket-action-plan-step--chat-highlight";
const HIGHLIGHT_DURATION_MS = 2200;

export default class extends Controller<HTMLElement> {
    private highlightTimeout: number | null = null;

    disconnect(): void {
        if (this.highlightTimeout !== null) {
            window.clearTimeout(this.highlightTimeout);
            this.highlightTimeout = null;
        }
    }

    highlightAction(event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        const stepIds = this.stepIdsFromTrigger(trigger);
        if (stepIds.length === 0) {
            return;
        }

        const steps = stepIds
            .map((stepId) => document.querySelector<HTMLElement>(this.stepSelector(stepId)))
            .filter((step): step is HTMLElement => step instanceof HTMLElement);

        if (steps.length === 0) {
            trigger.setAttribute("data-highlight-state", "missing");
            return;
        }

        trigger.setAttribute("data-highlight-state", "found");
        steps.forEach((step) => this.openStep(step));
        this.scrollStepIntoView(steps[0]);
        this.highlightSteps(steps);
    }

    private stepIdsFromTrigger(trigger: HTMLElement): string[] {
        const rawStepIds = trigger.dataset.stepIds || "";
        const splitStepIds = rawStepIds
            .split(",")
            .map((stepId) => stepId.trim())
            .filter(Boolean);

        if (splitStepIds.length > 0) {
            return Array.from(new Set(splitStepIds));
        }

        const legacyStepId = (trigger.dataset.stepId || "").trim();
        return legacyStepId ? [legacyStepId] : [];
    }

    private openStep(step: HTMLElement): void {
        const details = step.querySelector("details");
        if (details instanceof HTMLDetailsElement) {
            details.open = true;
        }
    }

    private scrollStepIntoView(step: HTMLElement): void {
        const viewport = document.querySelector<HTMLElement>("#tickets-detail-action-scroll");

        if (!viewport) {
            step.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        const elementRect = step.getBoundingClientRect();
        const viewportRect = viewport.getBoundingClientRect();
        const targetScrollTop = viewport.scrollTop + elementRect.top - viewportRect.top - 100;

        viewport.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: "smooth",
        });
    }

    private highlightSteps(steps: HTMLElement[]): void {
        document.querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}`).forEach((element) => {
            element.classList.remove(HIGHLIGHT_CLASS);
        });

        steps.forEach((step) => {
            step.classList.add(HIGHLIGHT_CLASS);
            step.setAttribute("tabindex", "-1");
        });
        steps[0]?.focus({ preventScroll: true });

        if (this.highlightTimeout !== null) {
            window.clearTimeout(this.highlightTimeout);
        }

        this.highlightTimeout = window.setTimeout(() => {
            steps.forEach((step) => step.classList.remove(HIGHLIGHT_CLASS));
            this.highlightTimeout = null;
        }, HIGHLIGHT_DURATION_MS);
    }

    private stepSelector(stepId: string): string {
        return `[data-testid="ticket-action-plan-step"][data-step-id="${this.escapeAttributeValue(stepId)}"]`;
    }

    private escapeAttributeValue(value: string): string {
        return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    }
}
